import { useRef, useState } from "react"

import { useIsomorphicLayoutEffect } from "@/lib/terminal/use-isomorphic-layout-effect"

// Greedy shortest-column bento masonry (ported from the Terminal reference's
// FileDataMasonry). Columns grow to fill the pane (flex: 1 1 0) but never exceed
// COL_MAX, even in the single-column state, and a 2nd column appears as soon as
// one more COL_TARGET-wide column
// fits — capped at 2, because 2-up is the intended shape. Each box, in source
// order, lands in whichever column is currently shortest, so columns pack with
// no vertical gaps. Heights are MEASURED from the rendered DOM (a box's height
// depends only on column width, which is fixed for a given column count, so the
// measurement stays stable across re-assignment). A ResizeObserver on the
// container drives the column count; a per-box ResizeObserver re-measures when
// rows expand/collapse or async data lands.

const COL_TARGET = 480 // min comfortable column width (drives the count)
const COL_MAX = 500 // a column never flex-grows wider than this
const COUNT_GAP = 24 // inter-column gap assumed by the count math

export type MasonryItem = { key: string; node: React.ReactNode }

export const FileDataMasonry = ({ items }: { items: MasonryItem[] }) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const boxRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const [cols, setCols] = useState(1)
  const [assignment, setAssignment] = useState<number[]>([])
  const [measureTick, setMeasureTick] = useState(0)

  const itemKeys = items.map((item) => item.key).join("|")

  // Column count follows pane width; recompute on container resize. Counts how
  // many >=COL_TARGET columns (+ gap) fit; columns then flex-fill the pane up to
  // COL_MAX, so they grow to use the width but never get too wide, and a new
  // column appears as soon as one more fits.
  useIsomorphicLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const recompute = () => {
      const width = el.clientWidth
      if (!width) return
      const next = Math.max(
        1,
        Math.min(2, Math.floor((width + COUNT_GAP) / (COL_TARGET + COUNT_GAP)))
      )
      setCols((current) => (current === next ? current : next))
    }
    recompute()
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(recompute)
    observer?.observe(el)
    return () => observer?.disconnect()
  }, [])

  // Re-measure when a box's height changes (collapsible rows, async data).
  // Re-subscribe on `cols` too: a column-count change remounts cells into new
  // DOM nodes, so the observer must re-attach to them (the old nodes detach and
  // stop firing otherwise).
  useIsomorphicLayoutEffect(() => {
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(() =>
      setMeasureTick((tick) => tick + 1)
    )
    boxRefs.current.forEach((node) => observer.observe(node))
    return () => observer.disconnect()
  }, [itemKeys, cols])

  // Greedy shortest-column packing over measured heights.
  useIsomorphicLayoutEffect(() => {
    const heights = new Array<number>(cols).fill(0)
    const next: number[] = []
    for (let index = 0; index < items.length; index++) {
      const node = boxRefs.current.get(items[index].key)
      const height = node ? node.offsetHeight : 0
      let shortest = 0
      for (let col = 1; col < cols; col++) {
        if (heights[col] < heights[shortest]) shortest = col
      }
      next[index] = shortest
      heights[shortest] += height
    }
    setAssignment((current) =>
      current.length === next.length && current.every((v, i) => v === next[i])
        ? current
        : next
    )
  }, [cols, itemKeys, measureTick])

  const setBoxRef = (key: string) => (node: HTMLDivElement | null) => {
    if (node) boxRefs.current.set(key, node)
    else boxRefs.current.delete(key)
  }

  return (
    <div
      ref={containerRef}
      className="flex items-start justify-start gap-(--terminal-space-block)"
    >
      {Array.from({ length: cols }, (_, colIndex) => (
        <div
          key={colIndex}
          className="flex min-w-0 flex-1 flex-col gap-(--terminal-space-block)"
          style={{ maxWidth: COL_MAX }}
        >
          {items.map((item, itemIndex) =>
            (assignment[itemIndex] ?? itemIndex % cols) === colIndex ? (
              <div
                key={item.key}
                ref={setBoxRef(item.key)}
                // Neutralize the section's own vertical margin so the column
                // `gap` is the single source of inter-box spacing.
                className="min-w-0 [&>section]:my-0"
              >
                {item.node}
              </div>
            ) : null
          )}
        </div>
      ))}
    </div>
  )
}
